import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import Space from '../../../components/xr/space';
import { CoordsSelectSpace } from '../space';
import { updateAppState, setLoadedSpace } from '../../../store/actions/app';
import GatewayService from '../../../services/gateway-service';
import * as GatewayEvents from '../../../common/constants/gateway-events';
import { useActions } from '../../../hooks';
import Stick from '../stick';
import { useFrame, useThree } from 'react-three-fiber';
import { PerspectiveCamera } from 'drei';
import { LookControls } from '../../../components/xr/controls';
import LookControlsImpl from '../../../common/three/look-controls';
import { NoToneMapping } from 'three';
import Loader from '../../../components/xr/loader';
import { isEqual } from 'lodash';

import { context } from '../../../config/context';
import { isMacLike } from '../../../common/device';
import { useSelector } from 'react-redux';
import { RootState } from '../../../types/data';

let prevRotation = {
  x: 0,
  y: 0,
  z: 0,
};

type TExperienceEnvironment = React.Props<{}> & {};

type TExperienceEnvironmentReduxState = {
  space: RootState['app']['space'];
  appState: RootState['app']['state'];
};

const ExperienceEnvironment = ({ children }: TExperienceEnvironment) => {
  const secondarySpaceId = 'bg-video-space';
  const { gl } = useThree();

  const {
    space: {
      current,
      loaded: spaceLoaded,
      history: [, currSpace],
    },
    appState: { coordsSelectEnabled, isStickEnabled, isContentVisible, showLoader },
  } = useSelector<RootState, TExperienceEnvironmentReduxState>(state => ({
    space: state.app.space,
    appState: state.app.state,
  }));

  const boundActions = useActions({
    updateAppState,
    setLoadedSpace,
  });

  const controls = useRef<LookControlsImpl>();

  const handleTick = useCallback(() => {
    // console.log(delta);
    if (controls.current) {
      // get camera rotation in each frame
      const rotation = controls.current.object3D.rotation;

      const data = {
        value: {
          x: rotation.x,
          y: rotation.y,
          z: rotation.z,
        },
      };

      if (!isEqual(data.value, prevRotation)) {
        // eit camera rotation with GatewayService for 2D animations
        GatewayService.emit(GatewayEvents.SYNC_CAMERA_ROTATION, data);
      }

      prevRotation = { ...data.value };
    }
  }, []);

  useFrame(handleTick);

  // useEffect(() => {
  //   if (isMacLike()) {
  //     // gl.toneMapping = NoToneMapping;
  //     // gl.outputEncoding = LinearEncoding;
  //   }
  // }, [gl]);

  const secondarySpaceData = useMemo(
    () => ({
      ...current,
      isPhoto: true,
    }),
    [current]
  );

  return (
    <context.Provider
      value={{
        controls: controls,
      }}>
      <group>
        <group position={[0, 0, 0]}>
          <PerspectiveCamera makeDefault args={[75]}>
            <group visible={showLoader || !spaceLoaded}>
              <Loader scale={[0.5, 0.5, 0.5]} position={[0, 0, -1]} />
            </group>
          </PerspectiveCamera>
        </group>
        <LookControls ref={controls} />

        <Space data={secondarySpaceData} resourceId={secondarySpaceId} radius={50} renderOrder={-2} />
        <Space
          data={currSpace}
          enableAnimation
          radius={45}
          renderOrder={-1}
          onLoad={() => boundActions.setLoadedSpace(currSpace)}
        />

        {coordsSelectEnabled ? <CoordsSelectSpace /> : null}

        {isStickEnabled ? <Stick /> : null}
        <group visible={isContentVisible}>{children}</group>
      </group>
    </context.Provider>
  );
};

export default ExperienceEnvironment;
